import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import {
  checkIsBovioEnvironmentEligible,
  checkIsBovioEnvironmentParams,
  getIsPageBoVioEligible
} from 'components/InitializeBoVioEnvironment/utils'
import {MAPBOX_DEFAULT_ZOOM} from 'config/map'
import {hasStickyInAppFirst} from 'hooks/useInAppFirst/sessionStore'
import {hasStickyOtaMode} from 'hooks/useOtaMode/sessionStore'
import handleFatalError from 'modules/common/actions/errors'
import {getLocationSearchTypeFromGeolocationSourceCookie} from 'utils/geolocation'
import {hasDates} from 'utils/trafficSource'
import {isFsmrInQueryString} from 'utils/url'

import {isBrowser} from '@vio/core/src/_web/utils/environment'
import {
  safelyGetStorageItem,
  safelySetStorageItem
} from '@vio/core/src/_web/utils/persistence'
import {type Lov} from '@vio/core/src/analytics/types/Events'
import {type OfferMode} from '@vio/core/src/profile/constants'
import {DateValidationReason} from '@vio/core/src/searchParams/dateValidationReason'
import {getPersistedGeolocationSource} from '@vio/core/src/utils/geolocation'

import {getOfferModeOverride, getQueryString, isDatesDefault} from './selectors'
import {LayoutType, LocationSearchType, SearchUrlParams} from './types'

import type {Hotel} from '@findhotel/sapi'
import type {Placement} from '@vio/core/src/deepLinking/types'
import type {Status as CallToBookStatus} from 'components/CallToBook/useCallToBook'
import type {AppThunk, RootState} from 'store'

export type {Lov, LovHit} from '@vio/core/src/analytics/types/Events'

export type AppLockedDealConfig =
  | {
      isAppLockedDealAudience: false
    }
  | {
      isAppLockedDealAudience: true
      placement: Placement
    }

export type SearchStateType = {
  activeHotelId: Hotel['objectID']
  error?: Array<Record<string, unknown>>
  isDefaultDatesSearch: boolean
  isUserSearch: boolean
  isHomeSearch: boolean
  isDestinationPageSearch: boolean
  isUserInteractedWithFilters: boolean
  isNoDatesSearch: boolean
  isLandingExperience: boolean
  isHomePageLandingExperience: boolean
  isMapOpen: boolean
  isFsmr: boolean
  isUserLocationSearch: boolean
  userLocationSearchType: LocationSearchType
  listOfValues: Lov | null | undefined
  queryString: string
  landingQueryString: string
  landingReferrer: string
  visitedHotels: Record<string, boolean>
  layout: LayoutType
  searchDesktopHeaderHeight: number
  isTopHeaderHidden: boolean
  showTotalPrices: boolean
  selectedAvailabilityDate: string
  isSplitBooking: boolean
  freeTextSearchString: string
  mapZoomLevel: number
  isBoVioEnvironment: boolean
  isAppLockedDealExcluded: boolean
  anchorAppLockedAudienceConfig: AppLockedDealConfig
  nonAnchorAppLockedAudienceConfig: AppLockedDealConfig
  dateValidationReason: DateValidationReason | null
  callToBook: {
    status: CallToBookStatus
    phoneNumber: string | null
    displayedPhoneNumber: string | null
    href: string | null
    payload: Record<string, unknown>
  }
  isAnchorHotelHidden?: boolean
  isUserClosedHostelAlert?: boolean
  isStickyOtaMode: boolean // Added as part of sc-183439 — set after the first landing with profile=23afbcc5e3 and persists for the browser session
  isStickyInAppFirst: boolean // Added as part of sc-185842 — set after the first landing with inAppFirst=1 and persists for the tab session
  offerModeOverride: OfferMode | null
}

export const initialState: SearchStateType = {
  activeHotelId: null,
  isDefaultDatesSearch: false,
  isUserSearch: false,
  isHomeSearch: false,
  isDestinationPageSearch: false,
  isUserInteractedWithFilters: false,
  isNoDatesSearch: false,
  isMapOpen: false,
  isFsmr: false,
  isUserLocationSearch: false,
  userLocationSearchType: undefined,
  isLandingExperience: false,
  isHomePageLandingExperience: false,
  listOfValues: undefined,
  queryString: undefined,
  landingQueryString: undefined,
  landingReferrer: '',
  visitedHotels: JSON.parse(
    safelyGetStorageItem(
      'sessionStorage',
      'visitedHotels',
      'getVisitedHotels failed'
    ) || '{}'
  ),
  layout: undefined,
  searchDesktopHeaderHeight: 0,
  isTopHeaderHidden: false,
  showTotalPrices: false,
  selectedAvailabilityDate: undefined,
  isSplitBooking: false,
  mapZoomLevel: MAPBOX_DEFAULT_ZOOM,
  freeTextSearchString: '',
  dateValidationReason: null,
  isBoVioEnvironment: false,
  isAppLockedDealExcluded: false,
  anchorAppLockedAudienceConfig: undefined,
  nonAnchorAppLockedAudienceConfig: undefined,
  callToBook: {
    status: 'loading',
    phoneNumber: null,
    displayedPhoneNumber: null,
    href: null,
    payload: {}
  },
  isAnchorHotelHidden: false,
  isUserClosedHostelAlert: false,
  // On the client, seed from sessionStorage (the client-side source of truth)
  // so a fresh store recovers the sticky flag for the current tab. See
  // hooks/useOtaMode/sessionStore. The cookie is read on the server only.
  isStickyOtaMode: !isBrowser ? false : hasStickyOtaMode(),
  // On the client, seed from sessionStorage (the single source of truth) so a
  // fresh store recovers the flag for the current tab. The server has no signal
  // (no cookie), so it always starts false. See hooks/useInAppFirst/sessionStore.
  isStickyInAppFirst: !isBrowser ? false : hasStickyInAppFirst(),
  offerModeOverride: null
}

export interface SyncStoreWithUrlPayload {
  queryString: string
  urlParams: SearchUrlParams
  isMobile: boolean
  isSearchBoxOpenByDefault?: boolean
  dateValidationReason?: DateValidationReason | null
}

interface SetListOfValuesPayload {
  listOfValues: Lov
}

interface SearchError {
  error?: Array<Record<string, unknown>>
}

const {actions, reducer} = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setListOfValues: (
      state: SearchStateType,
      {payload}: PayloadAction<SetListOfValuesPayload>
    ) => {
      state.listOfValues = payload.listOfValues
    },
    syncStoreWithUrl: (
      state: SearchStateType,
      {payload}: PayloadAction<SyncStoreWithUrlPayload>
    ) => {
      const {urlParams, queryString} = payload

      state.isDefaultDatesSearch = false
      state.isNoDatesSearch = false

      const currentQueryString = getQueryString({search: state} as RootState)
      const queryStringHasChanged = currentQueryString !== queryString

      // Only set new queryString when changed so we can memoize any selectors based on it
      if (queryStringHasChanged) {
        state.queryString = queryString
      }

      state.isUserSearch = urlParams.userSearch === '1'
      state.isHomeSearch = urlParams.homeSearch === '1'
      state.isDestinationPageSearch = urlParams.destinationPageSearch === '1'

      state.isFsmr = isFsmrInQueryString(queryString)

      // @search This is going to be addressed on the sc-167114
      // We don't want user located searches if hotelId or placeId or iataCode is present
      state.isUserLocationSearch =
        urlParams.userLocationSearch === '1' &&
        !urlParams.hotelId &&
        !urlParams.placeId &&
        !urlParams.iataCode

      if (state.isUserLocationSearch && !state.userLocationSearchType) {
        // We set whatever is the source from the cookie as default and only if there's no value already
        state.userLocationSearchType =
          getLocationSearchTypeFromGeolocationSourceCookie(
            getPersistedGeolocationSource()
          )
      } else if (!state.isUserLocationSearch) {
        state.userLocationSearchType = undefined
      }
      state.isUserInteractedWithFilters =
        urlParams.userInteractedWithFilters === '1'

      state.isLandingExperience = !state.isUserSearch
      if (state.isLandingExperience) {
        const isDefaultDatesSearch =
          isDatesDefault({search: state} as RootState) && !state.isUserSearch
        state.isDefaultDatesSearch = isDefaultDatesSearch
        state.isNoDatesSearch = !hasDates(urlParams)
      }

      state.dateValidationReason = payload.dateValidationReason ?? null

      if (urlParams.layout) state.layout = urlParams.layout
    },
    setVisitedHotel(state: SearchStateType, {payload}: PayloadAction<string>) {
      state.visitedHotels[payload] = true
    },
    setIsMapOpen(state: SearchStateType, {payload}: PayloadAction<boolean>) {
      state.isMapOpen = payload
    },
    setActiveHotelId(state: SearchStateType, {payload}: PayloadAction<string>) {
      state.activeHotelId = payload
    },
    setLayout(state: SearchStateType, {payload}: PayloadAction<LayoutType>) {
      state.layout = payload
    },
    setSearchDesktopHeaderHeight(
      state: SearchStateType,
      {payload}: PayloadAction<number>
    ) {
      state.searchDesktopHeaderHeight = payload
    },
    setIsTopHeaderHidden(
      state: SearchStateType,
      {payload}: PayloadAction<boolean>
    ) {
      state.isTopHeaderHidden = payload
    },
    setShowTotalPrices: (
      state: SearchStateType,
      {payload}: PayloadAction<boolean>
    ) => ({
      ...state,
      showTotalPrices: payload
    }),
    setUserLocationSearchType(
      state: SearchStateType,
      {payload}: PayloadAction<LocationSearchType>
    ) {
      state.userLocationSearchType = payload
    },
    setSelectedAvailabilityDate(
      state: SearchStateType,
      {payload}: PayloadAction<string>
    ) {
      state.selectedAvailabilityDate = payload
    },
    userSearchedFromHome(state: SearchStateType) {
      state.isHomeSearch = true
      state.isUserSearch = true
    },
    setMapZoomLevel: (
      state: SearchStateType,
      {payload}: PayloadAction<number>
    ) => ({
      ...state,
      mapZoomLevel: payload
    }),

    setIsBoVioEnvironment: (
      state: SearchStateType,
      {payload}: PayloadAction<boolean>
    ) => {
      state.isBoVioEnvironment = payload
    },
    setIsAppLockedDealExcluded: (
      state: SearchStateType,
      {payload}: PayloadAction<boolean>
    ) => {
      state.isAppLockedDealExcluded = payload
    },
    setAppLockedAudienceConfig: (
      state: SearchStateType,
      {
        payload
      }: PayloadAction<
        Pick<
          SearchStateType,
          'anchorAppLockedAudienceConfig' | 'nonAnchorAppLockedAudienceConfig'
        >
      >
    ) => {
      state.anchorAppLockedAudienceConfig =
        payload.anchorAppLockedAudienceConfig
      state.nonAnchorAppLockedAudienceConfig =
        payload.nonAnchorAppLockedAudienceConfig
    },
    setCallToBook: (
      state: SearchStateType,
      {payload}: PayloadAction<SearchStateType['callToBook']>
    ) => {
      state.callToBook = payload
    },
    setIsAnchorHotelHidden: (
      state: SearchStateType,
      {payload}: PayloadAction<boolean>
    ) => {
      state.isAnchorHotelHidden = payload
    },
    setIsUserClosedHostelAlert: (
      state: SearchStateType,
      {payload}: PayloadAction<boolean>
    ) => {
      state.isUserClosedHostelAlert = payload
    },
    setIsStickyOtaMode: (
      state: SearchStateType,
      {payload}: PayloadAction<boolean>
    ) => {
      state.isStickyOtaMode = payload
    },
    setIsStickyInAppFirst: (
      state: SearchStateType,
      {payload}: PayloadAction<boolean>
    ) => {
      state.isStickyInAppFirst = payload
    }
  },
  extraReducers: {
    [handleFatalError.type]: (
      state: SearchStateType,
      action: PayloadAction<SearchError>
    ) => {
      state.error = action.payload.error
    }
  }
})

export const setShowTotalPrices = (
  showTotalPrices: boolean,
  persist = true
) => {
  if (persist)
    safelySetStorageItem(
      localStorage,
      'showTotalPrices',
      showTotalPrices.toString()
    )
  return actions.setShowTotalPrices(showTotalPrices)
}

export const setVisitedHotel = (hotelId: string) => {
  if (hotelId) {
    const currentVisitedHotels = JSON.parse(
      safelyGetStorageItem(
        sessionStorage,
        'visitedHotels',
        'getVisitedHotels failed'
      ) || '{}'
    )

    currentVisitedHotels[hotelId] = true

    safelySetStorageItem(
      sessionStorage,
      'visitedHotels',
      JSON.stringify(currentVisitedHotels),
      'setVisitedHotel failed'
    )
  }

  return actions.setVisitedHotel(hotelId)
}

export const initializeBovioEnvironment =
  ({
    pageName,
    hidesAppLockedDeals,
    brandIsOtaMode,
    profileParam
  }: checkIsBovioEnvironmentParams): AppThunk =>
  (dispatch, getState) => {
    const offerModeOverride = getOfferModeOverride(getState())
    if (offerModeOverride && getIsPageBoVioEligible(pageName)) {
      dispatch(actions.setIsAppLockedDealExcluded(true))
      dispatch(setIsBoVioEnvironment(offerModeOverride !== 'meta'))
      return
    }

    const eligible = checkIsBovioEnvironmentEligible({
      pageName,
      hidesAppLockedDeals,
      brandIsOtaMode,
      profileParam
    })
    dispatch(actions.setIsAppLockedDealExcluded(eligible))
    if (eligible) dispatch(setIsBoVioEnvironment(Boolean(brandIsOtaMode)))
  }

export type SetListOfValuesAction = ReturnType<typeof actions.setListOfValues>

export const {
  syncStoreWithUrl,
  setListOfValues,
  setIsMapOpen,
  setActiveHotelId,
  setLayout,
  setSearchDesktopHeaderHeight,
  setIsTopHeaderHidden,
  setUserLocationSearchType,
  setSelectedAvailabilityDate,
  userSearchedFromHome,
  setMapZoomLevel,
  setIsBoVioEnvironment,
  setAppLockedAudienceConfig,
  setCallToBook,
  setIsAnchorHotelHidden,
  setIsUserClosedHostelAlert,
  setIsStickyOtaMode,
  setIsStickyInAppFirst // Added as part of sc-185842,
} = actions

export default reducer
